import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { PageHeader } from "~/components/page-header";
import { DataTable } from "~/components/data-table";
import { MasterDetail } from "~/components/master-detail";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Checkbox } from "~/components/ui/checkbox";
import { Card } from "~/components/ui/card";
import { useConfirm } from "~/components/confirm-dialog";
import { api, APIError, type FreighterRateInput } from "~/lib/api";
import { formatKES } from "~/lib/format";
import type { IFreighterRate } from "~/lib/proto";

export const Route = createFileRoute("/_authed/ops/rates")({
  component: Rates,
});

type Selection = IFreighterRate | "new" | null;

type FormState = {
  originCountry: string;
  mode: string;
  freighter: string;
  costPerKgUsd: string;
  markupPct: string;
  volumetricDivisor: string;
  fixedFeeUsd: string;
  minChargeUsd: string;
  pricePerM3Usd: string;
  daysMin: string;
  daysMax: string;
  active: boolean;
};

const EMPTY: FormState = { originCountry: "", mode: "air", freighter: "", costPerKgUsd: "", markupPct: "", volumetricDivisor: "", fixedFeeUsd: "", minChargeUsd: "", pricePerM3Usd: "", daysMin: "", daysMax: "", active: true };

function Rates() {
  const [selected, setSelected] = useState<Selection>(null);

  const { data: rows, isLoading } = useQuery({
    queryKey: ["ops", "rates"],
    queryFn: () => api.ops.listFreighterRates(),
  });

  const columns: ColumnDef<IFreighterRate, unknown>[] = [
    { header: "Route", accessorKey: "originCountry", cell: ({ row }) => <span className="font-medium">{row.original.originCountry} · {row.original.mode}</span>, meta: { headClassName: "w-[110px]" } },
    { header: "Freighter", accessorKey: "freighter", cell: ({ row }) => <span>{row.original.freighter}</span> },
    { header: "Cost/kg", accessorKey: "costPerKgUsd", cell: ({ row }) => <span className="tabular">${row.original.costPerKgUsd ?? "0"}</span>, meta: { headClassName: "w-[100px]" } },
    { header: "Markup", accessorKey: "markupPct", cell: ({ row }) => <span className="tabular">{row.original.markupPct ?? "0"}%</span>, meta: { headClassName: "w-[90px]" } },
    { header: "Est. /kg", accessorKey: "estPerKgKes", cell: ({ row }) => <span className="tabular">{formatKES(row.original.estPerKgKes)}</span>, meta: { headClassName: "w-[110px]" } },
    { header: "Active", accessorKey: "active", cell: ({ row }) => <Badge variant={row.original.active ? "success" : "secondary"}>{row.original.active ? "Active" : "Off"}</Badge>, meta: { headClassName: "w-[90px]" } },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Freighter rates"
        caption="Freighter quotes in USD; customer estimate = cost × FX × (1 + markup)"
        actions={<Button onClick={() => setSelected("new")}><Plus /> New rate</Button>}
      />
      <FxRateCard />
      <MasterDetail
        open={selected !== null}
        onClose={() => setSelected(null)}
        detail={selected ? <RatePanel row={selected === "new" ? null : selected} onClose={() => setSelected(null)} /> : null}
        emptyHint="Select a rate to edit it"
        title="Rate"
      >
        <DataTable
          data={rows}
          columns={columns}
          limit={rows?.length ?? 50}
          offset={0}
          onPageChange={() => {}}
          loading={isLoading}
          getRowId={(r) => String(r.id)}
          onRowClick={(r) => setSelected(r)}
          selectedRowId={selected && selected !== "new" ? String(selected.id) : undefined}
          emptyTitle="No rates yet"
          emptyBody="Add a rate to enable a route's estimate."
        />
      </MasterDetail>
    </>
  );
}

function FxRateCard() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data } = useQuery({ queryKey: ["ops", "settings"], queryFn: () => api.ops.getSettings() });
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data?.usdKesRate) setValue(String(data.usdKesRate));
  }, [data?.usdKesRate]);

  async function save() {
    const n = Number(value);
    if (!n || n <= 0) return;
    const ok = await confirm({
      title: "Update the exchange rate?",
      body: `This re-prices every customer estimate across all routes at ${n} KES per USD.`,
      confirmLabel: "Update rate",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.ops.setUsdKesRate(n);
      toast.success("Exchange rate updated");
      void qc.invalidateQueries({ queryKey: ["ops", "settings"] });
      void qc.invalidateQueries({ queryKey: ["ops", "rates"] });
    } catch (e) {
      toast.error(e instanceof APIError ? e.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mb-3 flex-row items-end gap-3 p-3.5">
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium">USD → KES exchange rate</p>
        <p className="text-text-faint text-[12px]">Drives every customer estimate. Freighter costs are quoted in USD.</p>
      </div>
      <div className="w-28 space-y-1.5">
        <Label htmlFor="fx">Rate</Label>
        <Input id="fx" type="number" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
      </div>
      <Button loading={busy} onClick={() => void save()}>Save</Button>
    </Card>
  );
}

function RatePanel({ row, onClose }: { row: IFreighterRate | null; onClose: () => void }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const editing = !!row;
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (row) {
      setForm({
        originCountry: row.originCountry ?? "",
        mode: row.mode ?? "air",
        freighter: row.freighter ?? "",
        costPerKgUsd: row.costPerKgUsd ?? "",
        markupPct: row.markupPct ?? "",
        volumetricDivisor: row.volumetricDivisor ? String(row.volumetricDivisor) : "",
        fixedFeeUsd: row.fixedFeeUsd ?? "",
        minChargeUsd: row.minChargeUsd ?? "",
        pricePerM3Usd: row.pricePerM3Usd ?? "",
        daysMin: row.daysMin ? String(row.daysMin) : "",
        daysMax: row.daysMax ? String(row.daysMax) : "",
        active: row.active ?? true,
      });
    } else {
      setForm(EMPTY);
    }
  }, [row]);

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    const ok = await confirm({
      title: editing ? "Save rate changes?" : "Create this rate?",
      body: "This changes the estimate customers see for this route.",
      confirmLabel: editing ? "Save changes" : "Create rate",
    });
    if (!ok) return;
    setBusy(true);
    const payload: FreighterRateInput = {
      originCountry: form.originCountry,
      mode: form.mode,
      freighter: form.freighter,
      costPerKgUsd: form.costPerKgUsd || undefined,
      markupPct: form.markupPct || undefined,
      volumetricDivisor: form.volumetricDivisor ? Number(form.volumetricDivisor) : undefined,
      fixedFeeUsd: form.fixedFeeUsd || undefined,
      minChargeUsd: form.minChargeUsd || undefined,
      pricePerM3Usd: form.pricePerM3Usd || undefined,
      daysMin: form.daysMin ? Number(form.daysMin) : undefined,
      daysMax: form.daysMax ? Number(form.daysMax) : undefined,
      active: form.active,
    };
    try {
      if (editing) await api.ops.updateFreighterRate(String(row!.id), payload);
      else await api.ops.createFreighterRate(payload);
      toast.success(editing ? "Rate updated" : "Rate created");
      void qc.invalidateQueries({ queryKey: ["ops", "rates"] });
      onClose();
    } catch (e) {
      toast.error(e instanceof APIError ? e.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  const { data: settings } = useQuery({ queryKey: ["ops", "settings"], queryFn: () => api.ops.getSettings() });
  const fx = settings?.usdKesRate ?? 130;
  const costUsd = Number(form.costPerKgUsd);
  const perM3Usd = Number(form.pricePerM3Usd);
  const markupFactor = fx * (1 + Number(form.markupPct || 0) / 100) * 100;
  const est =
    perM3Usd > 0
      ? `${formatKES(Math.round(perM3Usd * markupFactor))}/m³`
      : costUsd > 0
        ? `${formatKES(Math.round(costUsd * markupFactor))}/kg`
        : null;

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="space-y-4 p-4 pr-10">
        <div>
          <p className="eyebrow">Rate</p>
          <h2 className="section-heading mt-0.5">{editing ? `${row!.originCountry} · ${row!.mode}` : "New rate"}</h2>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Origin</Label><Input value={form.originCountry} disabled={editing} onChange={(e) => set("originCountry", e.target.value.toUpperCase())} placeholder="US" /></div>
          <div className="space-y-1.5"><Label>Mode</Label><Input value={form.mode} disabled={editing} onChange={(e) => set("mode", e.target.value.toLowerCase())} placeholder="air" /></div>
        </div>
        <div className="space-y-1.5"><Label>Freighter</Label><Input value={form.freighter} onChange={(e) => set("freighter", e.target.value)} placeholder="KeBay" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Cost/kg (USD)</Label><Input type="number" value={form.costPerKgUsd} onChange={(e) => set("costPerKgUsd", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Markup %</Label><Input type="number" value={form.markupPct} onChange={(e) => set("markupPct", e.target.value)} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Volumetric ÷</Label><Input type="number" value={form.volumetricDivisor} onChange={(e) => set("volumetricDivisor", e.target.value)} placeholder="6000 (blank = actual wt)" /></div>
          <div className="space-y-1.5"><Label>Handling fee (USD)</Label><Input type="number" value={form.fixedFeeUsd} onChange={(e) => set("fixedFeeUsd", e.target.value)} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Min charge (USD)</Label><Input type="number" value={form.minChargeUsd} onChange={(e) => set("minChargeUsd", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Per m³ (USD)</Label><Input type="number" value={form.pricePerM3Usd} onChange={(e) => set("pricePerM3Usd", e.target.value)} placeholder="Ocean per-volume" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Days min</Label><Input type="number" value={form.daysMin} onChange={(e) => set("daysMin", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Days max</Label><Input type="number" value={form.daysMax} onChange={(e) => set("daysMax", e.target.value)} /></div>
        </div>

        {editing && (
          <label className="flex items-center gap-2.5">
            <Checkbox checked={form.active} onCheckedChange={(v) => set("active", v === true)} />
            <span className="text-[13px]">Active (offered to customers)</span>
          </label>
        )}

        {est && <p className="text-text-faint text-[12px]">Customer estimate ≈ {est} at the current exchange rate.</p>}

        <Button className="w-full" loading={busy} onClick={() => void save()}>{editing ? "Save changes" : "Create rate"}</Button>
      </div>
    </div>
  );
}
